import { useEffect, useState } from "react";
import { DefaultPagination } from "../../constants/generalConstants";
import * as dashboardServices from "../../services/dashboard";
import { LocationStatusRecord } from "../../shared/interface/locationStatusList.interface";
import useValue from "../../hooks/useValue";
import useTablePagination from "../../hooks/useTablePagination";
import { CustomNotification } from "../../util/CustomNotification";
import { GWStatusConstants, PLCStatusConstants } from "../../constants/DatabaseConstants/ParkingConstants";

export const useLocationStatusList = () => {
  /* CONSTANTS */

  /* STATE */
  const [searchedText, setSearchedText] = useState<string>("");
  const [locationList, setLocationList] = useState<LocationStatusRecord[]>([]);
  const [reFetch, setRefetch] = useState(false);

  /* HOOK */
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  /* HANDLERS */
  let startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleRefreshList = () => {
    handleResetPagination();
    setRefetch(true);
  };

  const handleChangeOnSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleResetPagination();
    if (!value) setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    const trimmedValue = value?.trimStart();
    setSearchedText(trimmedValue);
    handleRefreshList();
  };

  const getStatus = (status: number) => {
    let statusText = "No device found";

    if (status === GWStatusConstants.CONNECTED || status === PLCStatusConstants.CONNECTED) {
      statusText = "Connected";
    } else if (status === GWStatusConstants.DISCONNECTED || status === PLCStatusConstants.DISCONNECTED) {
      statusText = "Disconnected";
    } else if (status === PLCStatusConstants.GW_INACTIVE) {
      statusText = "Gateway Inactive";
    }
    return statusText;
  };

  const getStatusColor = (status: string) => {
    let statusColor: "green" | "red" | "default" | string = "default";

    if (status === "Connected") {
      statusColor = "green";
    } else if (status === "Disconnected") {
      statusColor = "red";
    } else if (status === "Gateway Inactive") {
      statusColor = "red";
    }
    return statusColor;
  };

  const fetchLocationStatusList = async () => {
    try {
      handleEnableLoading();

      const paramsObject = {
        searchQuery: searchedText,
        page: pagination?.current || DefaultPagination.PAGE,
        pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE
      };

      const response = await dashboardServices.getLocationPLCGatewayStatusList(paramsObject);

      const formattedResponse = response?.data?.rows?.map((item, index) => {
        return {
          ...item,
          sr_no: startIndex + index + 1,
          plcStatus: getStatus(item?.plcHeartBeatLogs?.[0]?.device_status),
          gatewayStatus: getStatus(item?.gwHeartBeatLogs?.[0]?.device_status),
          lastIotHubLogTime: item?.iotHubDeviceToCloudLogs?.[0]?.created_on ?? null
        };
      });

      handleChangeTotalSize(response?.data?.count || 0);
      setLocationList(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setRefetch(false);
      handleDisableLoading();
    }
  };

  /* EFFECT */
  useEffect(() => {
    if (pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize]);

  useEffect(() => {
    if (reFetch) fetchLocationStatusList();
  }, [reFetch]);

  /* Debounced search */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchedText) setRefetch(true);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchedText]);

  return { getStatusColor, loading, handleChangeOnSearch, handleRefreshList, searchedText, locationList, pagination, handleChangePagination, handleChangeSearch };
};
